import { z } from 'zod'

// The events master (ADR 0082): BTownTix intake, POS and BTownTix places, and
// the POS events feed (build plan §4.3, schema bre.pos-events.v1).

export const EVENT_INTAKE_SYSTEM_SUB = 'system:event-intake'
export const POS_FEED_SYSTEM_SUB = 'system:pos-feed'
export const LEDGER_SYSTEM_SUB = 'system:ledger'
// The rentals service's task move (ADR 0077, plan 7.17).
export const RENTAL_TASKS_SYSTEM_SUB = 'system:rental-tasks'
export const EVENT_INTAKE_PERSON_ID = 'per_system_event_intake'

export const POS_EVENTS_SCHEMA = 'bre.pos-events.v1'
export const POS_EVENTS_MAX_DAYS = 7

export const PosFeedEvent = z.object({
  hubEventId: z.string(),
  version: z.number().int(),
  name: z.string(),
  // The night's parent, for a label: "Riverfront Rampage 2027".
  parentName: z.string().nullable(),
  kind: z.string().nullable(),
  status: z.enum(['planned', 'confirmed', 'completed', 'cancelled']),
  startsOn: z.string(),
  endsOn: z.string(),
  serviceStartsLocal: z.string().nullable(),
  serviceEndsLocal: z.string().nullable(),
  spaceId: z.string().nullable(),
  qboProjectId: z.string().nullable().optional(),
  qboProjectName: z.string().nullable().optional(),
})
export type PosFeedEvent = z.infer<typeof PosFeedEvent>

export const PosEventsFeed = z.object({
  schema: z.literal(POS_EVENTS_SCHEMA),
  asOf: z.string(),
  location: z.object({ id: z.string(), facilityId: z.string(), spaceId: z.string().nullable() }),
  registers: z.array(z.object({ registerId: z.string(), spaceId: z.string() })),
  events: z.array(PosFeedEvent),
  // False when the ledger didn't answer: project fields are left out and POS
  // keeps what it has. An empty project is not the same as an unknown one.
  projectsFresh: z.boolean(),
})
export type PosEventsFeed = z.infer<typeof PosEventsFeed>

// What the web Worker reads from BTownTix and hands to core for intake.
export const IntakePerformance = z.object({
  id: z.string().min(1).max(200),
  startsAt: z.string().datetime(),
  doorsAt: z.string().datetime().nullable(),
  status: z.string().max(60),
  venueName: z.string().max(300).nullable(),
})
export const IntakeEvent = z.object({
  sourceId: z.string().min(1).max(200),
  title: z.string().min(1).max(300),
  status: z.string().max(60),
  startsAt: z.string().datetime().nullable(),
  endsAt: z.string().datetime().nullable(),
  performances: z.array(IntakePerformance).max(400),
})
export type IntakeEvent = z.infer<typeof IntakeEvent>
export const IntakeRun = z.object({ asOf: z.string().datetime(), events: z.array(IntakeEvent).max(1000) })

export type IntakeCounts = { read: number; created: number; updated: number; suggested: number; skipped: number; flagged: number; errors: string[] }

// Whether BTownTix's status word means the event is out in the world.
const LIVE_WORDS = ['ON_SALE', 'PUBLISHED', 'SOLD_OUT', 'OFF_SALE', 'ENDED', 'COMPLETED', 'CANCELLED', 'CANCELED', 'POSTPONED']
export const isPublishedStatus = (status: string) => LIVE_WORDS.includes(status.toUpperCase())
export const isCanceledStatus = (status: string) => ['CANCELLED', 'CANCELED'].includes(status.toUpperCase())

// Chicago wall-clock for a UTC instant: YYYY-MM-DDTHH:MM.
export function chicagoLocal(iso: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      .formatToParts(new Date(iso))
      .map((p) => [p.type, p.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

const shiftIso = (iso: string, minutes: number) => new Date(Date.parse(iso) + minutes * 60_000).toISOString()

// The hours sales belong to an event: doors minus 2 hours to end plus 3 hours.
export function serviceWindow(perf: { startsAt: string; doorsAt: string | null }, endsAt: string | null): { startsLocal: string; endsLocal: string } {
  const opens = perf.doorsAt ?? perf.startsAt
  const ends = endsAt && Date.parse(endsAt) > Date.parse(perf.startsAt) ? endsAt : shiftIso(perf.startsAt, 180)
  return { startsLocal: chicagoLocal(shiftIso(opens, -120)), endsLocal: chicagoLocal(shiftIso(ends, 180)) }
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const weekdayOf = (date: string) => WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]!
export const nightName = (parent: string, date: string) => `${parent} · ${weekdayOf(date)}`
